import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck, Briefcase, Clock, Activity, Check, FolderOpen, Zap, TrendingUp, Trash2 } from "lucide-react";
import { cn } from "../../utils/cn";
import { LanePill } from "../ui/LanePill";
import { ScoreInput } from "../ui/ScoreInput";
import { UserMultiSelect } from "../ui/UserMultiSelect";
import { EpicScoreTrend } from "../ui/TrendSparkline";
import { ConfirmModal } from "./ConfirmModal";
import { scoreEpic } from "../../utils/scoring";
import { getLastActivityLabel, getStaleLevel } from "../../utils/velocity";
import { Epic, Project, Session, User, UserRole } from "../../types";

type StaleLevel = 'fresh' | 'aging' | 'stale' | 'critical';

const staleLevelColors: Record<StaleLevel, string> = {
    fresh: 'text-siemens-feedback-green',
    aging: 'text-siemens-feedback-orange',
    stale: 'text-red-500',
    critical: 'text-red-700'
};

interface DetailDrawerProps {
    epic?: Epic | null;
    isOpen: boolean;
    onClose: () => void;
    onUpdate?: (id: string, updates: Partial<Epic>) => void;
    onDelete?: (id: string) => void;
    previousScore?: number | null;
    projects?: Project[];
    sessions?: Session[];
    users?: User[];
    userRole?: UserRole;
}

/**
 * DetailDrawer - Slide-out panel for viewing and editing epic details
 */
export function DetailDrawer({ epic, isOpen, onClose, onUpdate, onDelete, projects, sessions, users = [], userRole }: DetailDrawerProps) {
    if (!epic) return null;

    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const staleLevel = getStaleLevel(epic) as StaleLevel;

    // Field restriction flags for Owner role
    const isOwner = userRole === 'owner';
    const canEditLane = onUpdate && !isOwner;
    const canEditProject = onUpdate && !isOwner;
    const canEditAssignments = onUpdate && !isOwner;

    // Calculate display score (fallback to on-the-fly calc if missing or 0)
    const storedScore = epic.wsjf;
    const displayedScore = (storedScore && storedScore !== 0) ? storedScore : scoreEpic(epic).wsjf;

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = () => {
        if (onDelete) {
            onDelete(epic.id);
            setShowDeleteConfirm(false);
            onClose();
        }
    };

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Mobile overlay backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="md:hidden fixed inset-0 bg-black/50 z-40"
                            onClick={onClose}
                        />
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="fixed inset-y-0 right-0 w-full md:w-[500px] z-50 bg-white dark:bg-slate-900 border-l border-black/5 dark:border-white/10 shadow-2xl flex flex-col"
                        >
                            {/* Header */}
                            <div className="p-4 md:p-6 border-b border-black/5 dark:border-white/5 flex items-start justify-between bg-slate-50/50 dark:bg-slate-950/50">
                                <div className="space-y-2 flex-1 pr-4">
                                    {canEditLane ? (
                                        <select
                                            className="bg-transparent border-none p-0 text-xs font-bold uppercase tracking-widest cursor-pointer focus:ring-0"
                                            value={epic.lane}
                                            onChange={(e) => {
                                                const newLane = e.target.value as Epic['lane'];
                                                const updates: Partial<Epic> = { lane: newLane };
                                                // Moving to Done → set progress to 100%
                                                if (newLane === 'Done' && epic.lane !== 'Done') {
                                                    updates.progress = 100;
                                                    updates.completedAt = new Date().toISOString();
                                                }
                                                // Moving from Done → reset progress to 0%
                                                if (newLane !== 'Done' && epic.lane === 'Done') {
                                                    updates.progress = 0;
                                                    updates.completedAt = undefined;
                                                }
                                                onUpdate!(epic.id, updates);
                                            }}
                                        >
                                            <option value="Unassigned">Unassigned</option>
                                            <option value="Now">Now</option>
                                            <option value="Next">Next</option>
                                            <option value="Later">Later</option>
                                            <option value="Done">Done</option>
                                        </select>
                                    ) : (
                                        <LanePill lane={epic.lane} />
                                    )}
                                    {onUpdate && !isOwner ? (
                                        <textarea
                                            className="w-full bg-transparent border-0 border-b border-transparent hover:border-slate-300 dark:hover:border-slate-700 focus:border-siemens-petrol focus:ring-0 p-0 text-lg md:text-xl font-black text-slate-900 dark:text-white leading-tight resize-none"
                                            rows={2}
                                            value={epic.title}
                                            onChange={(e) => onUpdate(epic.id, { title: e.target.value })}
                                        />
                                    ) : (
                                        <h2 className="text-lg md:text-xl font-black text-slate-900 dark:text-white leading-tight">{epic.title}</h2>
                                    )}
                                    <div className="flex items-center gap-4 mt-2">
                                        {isOwner && (
                                            <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">Some fields are read-only for your role</p>
                                        )}
                                        <button
                                            onClick={onUpdate && !isOwner ? () => onUpdate(epic.id, { sgm: !epic.sgm }) : undefined}
                                            disabled={!onUpdate || isOwner}
                                            className={cn(
                                                "flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold transition-colors",
                                                epic.sgm
                                                    ? "bg-siemens-petrol/10 text-siemens-petrol"
                                                    : "bg-slate-100 text-slate-400 hover:bg-slate-200",
                                                (!onUpdate || isOwner) && "opacity-50 cursor-not-allowed"
                                            )}
                                        >
                                            <ShieldCheck className="w-3 h-3" />
                                            SGM Direction
                                            {epic.sgm && <Check className="w-3 h-3 ml-0.5" />}
                                        </button>
                                    </div>
                                </div>
                                <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg flex-shrink-0">
                                    <X className="w-5 h-5 text-slate-500" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 md:space-y-8 custom-scrollbar">
                                {/* Scores */}
                                <div className="space-y-3 md:space-y-4">
                                    <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 border-b pb-2">Scoring Drivers</h3>
                                    <ScoreInput label="Security" value={epic.security ?? 3} onChange={onUpdate ? (v => onUpdate(epic.id, { security: v })) : undefined} icon={<ShieldCheck className="w-4 h-4" />} />
                                    <ScoreInput label="Business Value" value={epic.business ?? 3} onChange={onUpdate ? (v => onUpdate(epic.id, { business: v })) : undefined} icon={<Briefcase className="w-4 h-4" />} />
                                    <ScoreInput label="Time Criticality" value={epic.time ?? 3} onChange={onUpdate ? (v => onUpdate(epic.id, { time: v })) : undefined} icon={<Clock className="w-4 h-4" />} />
                                    <ScoreInput label="Ops Efficiency" value={epic.ops ?? 3} onChange={onUpdate ? (v => onUpdate(epic.id, { ops: v })) : undefined} icon={<Activity className="w-4 h-4" />} />
                                    <ScoreInput label="Confidence" value={epic.confidence ?? 3} onChange={onUpdate ? (v => onUpdate(epic.id, { confidence: v })) : undefined} icon={<Check className="w-4 h-4" />} />
                                    <ScoreInput label="Job Size (Effort)" value={epic.effort ?? 2} onChange={onUpdate ? (v => onUpdate(epic.id, { effort: v as 1 | 2 | 3 | 4 | 5 })) : undefined} icon={<Zap className="w-4 h-4" />} />
                                </div>

                                {/* Score Trend */}
                                {sessions && sessions.length >= 2 && (
                                    <div className="space-y-3 md:space-y-4">
                                        <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 border-b pb-2 flex items-center gap-2">
                                            <TrendingUp className="w-3 h-3" /> Score History
                                        </h3>
                                        <div className="flex items-center gap-4">
                                            <EpicScoreTrend epicId={epic.id} sessions={sessions} width={120} height={32} />
                                            <div className="text-xs text-siemens-text-soft">
                                                Showing last {Math.min(sessions.length, 8)} sessions
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Metadata */}
                                <div className="space-y-3 md:space-y-4">
                                    <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 border-b pb-2">Properties</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                        <div className="md:col-span-2">
                                            <UserMultiSelect
                                                label="Service Owners"
                                                selectedUserIds={Array.isArray(epic.so) ? epic.so : (epic.so ? [epic.so] : [])}
                                                users={users}
                                                onChange={canEditAssignments ? (ids => onUpdate!(epic.id, { so: ids })) : undefined}
                                                placeholder="Select service owners..."
                                                disabled={!canEditAssignments}
                                            />
                                            {isOwner && <p className="text-[9px] text-slate-400 mt-1">Contact admin to change assignments</p>}
                                        </div>
                                        <div className="md:col-span-2">
                                            <UserMultiSelect
                                                label="Architects"
                                                selectedUserIds={Array.isArray(epic.arq) ? epic.arq : (epic.arq ? [epic.arq] : [])}
                                                users={users}
                                                onChange={canEditAssignments ? (ids => onUpdate!(epic.id, { arq: ids })) : undefined}
                                                placeholder="Select architects..."
                                                disabled={!canEditAssignments}
                                            />
                                        </div>
                                        {/* Project Assignment */}
                                        <div className="md:col-span-2">
                                            <label className="text-[10px] font-bold uppercase text-slate-500 flex items-center gap-1">
                                                <FolderOpen className="w-3 h-3" /> Project
                                            </label>
                                            <select
                                                className="w-full bg-slate-100 dark:bg-slate-800 border-none rounded-lg px-3 py-2 text-xs font-bold cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                                value={epic.projectId || 'project-legacy'}
                                                onChange={canEditProject ? (e => onUpdate!(epic.id, { projectId: e.target.value })) : undefined}
                                                disabled={!canEditProject}
                                            >
                                                {projects?.map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Last Activity */}
                                        <div>
                                            <label className="text-[10px] font-bold uppercase text-slate-500 flex items-center gap-1">
                                                <Clock className="w-3 h-3" /> Last Updated
                                            </label>
                                            <div className={`text-xs font-bold ${staleLevelColors[staleLevel]}`}>
                                                {getLastActivityLabel(epic)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Progress */}
                                <div className="space-y-3 md:space-y-4">
                                    <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 border-b pb-2">Progress</h3>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <label className="text-[10px] font-bold uppercase text-slate-500">Completion</label>
                                            <span className="text-lg font-black text-siemens-petrol">{epic.progress ?? 0}%</span>
                                        </div>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            step="5"
                                            value={epic.progress ?? 0}
                                            onChange={onUpdate ? (e => {
                                                const newProgress = parseInt(e.target.value);
                                                const updates: Partial<Epic> = { progress: newProgress };
                                                // Auto-complete when set to 100%
                                                if (newProgress === 100 && epic.lane !== 'Done') {
                                                    updates.lane = 'Done';
                                                    updates.completedAt = new Date().toISOString();
                                                }
                                                onUpdate(epic.id, updates);
                                            }) : undefined}
                                            disabled={!onUpdate}
                                            className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full appearance-none cursor-pointer accent-siemens-petrol disabled:opacity-50"
                                        />
                                        <div className="flex justify-between text-[9px] text-slate-400">
                                            <span>Not Started</span>
                                            <span>In Progress</span>
                                            <span>Complete</span>
                                        </div>
                                        {epic.completedAt && (
                                            <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                                                ✓ Completed on {new Date(epic.completedAt).toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Notes */}
                                <div className="space-y-3 md:space-y-4">
                                    <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 border-b pb-2">Strategic Context</h3>
                                    <textarea
                                        className="w-full h-32 md:h-40 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3 md:p-4 text-xs font-medium focus:ring-siemens-petrol"
                                        value={epic.notes || ""}
                                        onChange={onUpdate ? (e => onUpdate(epic.id, { notes: e.target.value })) : undefined}
                                        placeholder="Strategic reasoning..."
                                        readOnly={!onUpdate}
                                    />
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="p-3 md:p-4 border-t border-black/5 dark:border-white/5 bg-slate-50 dark:bg-slate-950 flex items-center justify-between">
                                {onDelete ? (
                                    <button
                                        onClick={handleDeleteClick}
                                        className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-xs font-bold transition-colors flex items-center gap-2"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete Epic
                                    </button>
                                ) : (
                                    <div />
                                )}
                                <div className="flex items-center gap-2">
                                    <div className="text-xs font-bold text-slate-500">WSJF Score</div>
                                    <div className="text-xl md:text-2xl font-black text-siemens-petrol">{(displayedScore || 0).toFixed(2)}</div>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
            <ConfirmModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Epic?"
                message={
                    <span>
                        Are you sure you want to delete <span className="font-bold">{epic.title}</span>?
                        <br /><br />
                        This action cannot be undone.
                    </span>
                }
                variant="danger"
                confirmLabel="Delete Epic"
            />
        </>
    );
}

export default DetailDrawer;
